import type {
  ConsentRecord,
  Customer,
  CustomerGroup,
  FlagAuditEntry,
  LifecycleState,
  RiskFlag,
  RiskStatus,
  SavedCard,
  Segment,
  ToastItem,
} from '../../components/customers/data/types';

/** ─── Action type constants ─── */
export const CUSTOMERS_ADD = 'customers/ADD';
export const CUSTOMERS_UPDATE = 'customers/UPDATE';
export const CUSTOMERS_REMOVE = 'customers/REMOVE';
export const CUSTOMERS_SET_FLAG = 'customers/SET_FLAG';
export const CUSTOMERS_CLEAR_FLAG = 'customers/CLEAR_FLAG';
export const CUSTOMERS_RECORD_OPT_OUT = 'customers/RECORD_OPT_OUT';

// V2 — customer data operations
export const CUSTOMERS_BULK_TAG = 'customers/BULK_TAG';
export const CUSTOMERS_SET_LIFECYCLE = 'customers/SET_LIFECYCLE';
export const CUSTOMERS_MERGE = 'customers/MERGE';
export const CUSTOMERS_IMPORT = 'customers/IMPORT';

// V2 — groups
export const GROUPS_ADD = 'customers/GROUPS_ADD';
export const GROUPS_UPDATE = 'customers/GROUPS_UPDATE';
export const GROUPS_REMOVE = 'customers/GROUPS_REMOVE';
export const GROUPS_SET_MEMBERS = 'customers/GROUPS_SET_MEMBERS';
export const GROUPS_BULK_ADD = 'customers/GROUPS_BULK_ADD';

// V2 — segments
export const SEGMENTS_ADD = 'customers/SEGMENTS_ADD';
export const SEGMENTS_UPDATE = 'customers/SEGMENTS_UPDATE';
export const SEGMENTS_REMOVE = 'customers/SEGMENTS_REMOVE';

// P2 — saved cards
export const CARD_ADD = 'customers/CARD_ADD';
export const CARD_REMOVE = 'customers/CARD_REMOVE';
export const CARD_SET_DEFAULT = 'customers/CARD_SET_DEFAULT';

export const CUSTOMERS_TOAST_ADD = 'customers/TOAST_ADD';
export const CUSTOMERS_TOAST_REMOVE = 'customers/TOAST_REMOVE';

/** ─── State shape ─── */
export interface CustomersState {
  customers: Customer[];
  groups: CustomerGroup[];
  segments: Segment[];
  toasts: ToastItem[];
}

/** ─── Action payloads ─── */
export interface SetFlagPayload {
  id: string;
  flag: RiskFlag;
  newStatus: RiskStatus;
  auditEntry: FlagAuditEntry;
}

export interface ClearFlagPayload {
  id: string;
  auditEntry: FlagAuditEntry;
}

export interface RecordOptOutPayload {
  id: string;
  record: ConsentRecord;
}

export interface UpdateCustomerPayload {
  id: string;
  updates: Partial<Customer>;
}

export interface BulkTagPayload {
  ids: string[];
  tags: string[];
}

export interface SetLifecyclePayload {
  ids: string[];
  state: LifecycleState;
}

export interface MergePayload {
  keepId: string;
  merged: Customer;
  removeIds: string[];
}

export interface UpdateGroupPayload {
  id: string;
  updates: Partial<CustomerGroup>;
}

export interface SetGroupMembersPayload {
  groupId: string;
  memberIds: string[];
}

export interface BulkAddToGroupPayload {
  groupId: string;
  ids: string[];
}

export interface UpdateSegmentPayload {
  id: string;
  updates: Partial<Segment>;
}

export interface AddCardPayload {
  customerId: string;
  card: SavedCard;
}

export interface CardRefPayload {
  customerId: string;
  cardId: string;
}

/** Domain types other features read through the Redux layer, never from the Customers component tree. */
export type {
  Channel,
  ConsentRecord,
  ConsentSource,
  ConsentStatus,
  Customer,
  CustomerGroup,
  CustomerOrder,
  Daypart,
  OrderChannel,
  RuleCondition,
  RuleField,
  RuleGroup,
  RuleNode,
  RuleOperator,
  RuleOption,
  RuleValue,
  Segment,
  SegmentType,
} from '../../components/customers/data/types';
export { CHANNELS } from '../../components/customers/data/types';
export type { AudienceCounts, CampaignHistory } from './customerSegmentEngine';
